/**
 * Financial utility functions for cost formatting
 */

/**
 * Finnish VAT (ALV) percentage
 * As of September 2024, Finnish standard VAT rate is 25.5%
 */
export const VAT_PERCENTAGE = 25.5

/**
 * Human-readable VAT label for display (e.g., "sis. ALV 25,5%")
 * Single source of truth - use this instead of hardcoding the string
 */
export const VAT_LABEL = `sis. ALV ${VAT_PERCENTAGE.toString().replace('.', ',')}%`

/**
 * Human-readable VAT0 label for display (e.g., "ALV 0%")
 * Single source of truth for VAT-exclusive price labels
 */
export const VAT0_LABEL = 'ALV 0%'

/**
 * VAT multiplier for calculating VAT-inclusive prices
 * e.g., 1.255 for 25.5% VAT
 */
export const VAT_MULTIPLIER = 1 + VAT_PERCENTAGE / 100

/**
 * Removes VAT from a VAT-inclusive price
 * @param priceWithVat - Price including VAT
 * @returns Price excluding VAT
 */
export function removeVat(priceWithVat: number): number {
  return priceWithVat / VAT_MULTIPLIER
}

/**
 * Adds VAT to a VAT-exclusive price
 * @param priceWithoutVat - Price excluding VAT
 * @returns Price including VAT
 */
export function addVat(priceWithoutVat: number): number {
  return priceWithoutVat * VAT_MULTIPLIER
}

/**
 * Formats euros with Finnish locale formatting
 * @param euros - Amount in euros (can be string from database decimal or number)
 * @returns Formatted string (e.g., "150,00 €")
 */
export function formatEuros(euros: string | number | null | undefined): string {
  if (euros === null || euros === undefined) return '0,00 €'
  const amount = typeof euros === 'string' ? parseFloat(euros) : euros
  return `${amount.toFixed(2).replace('.', ',')} €`
}

/**
 * Formats a number as Finnish currency (whole euros, no decimals)
 * Uses Intl.NumberFormat for proper locale formatting
 * @param value - Amount in euros
 * @returns Formatted string (e.g., "1 234 €")
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

/**
 * Formats a number as Finnish currency with 2 decimal places
 * @param value - Amount in euros
 * @returns Formatted string (e.g., "1 234,56 €")
 */
export function formatCurrencyPrecise(value: number): string {
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Formats a VAT-inclusive amount showing both with and without VAT
 * @param valueWithVat - Amount including VAT
 * @returns Object with formatted withVat, withoutVat, and vatLabel strings
 */
export function formatCurrencyWithVat(valueWithVat: number): {
  withVat: string
  withoutVat: string
  vatLabel: string
} {
  return {
    withVat: formatCurrencyPrecise(valueWithVat),
    withoutVat: formatCurrencyPrecise(removeVat(valueWithVat)),
    vatLabel: VAT_LABEL,
  }
}

/**
 * Formats a VAT-exclusive amount showing both with and without VAT
 * @param valueWithoutVat - Amount excluding VAT
 * @returns Object with formatted withVat, withoutVat, and vatLabel strings
 */
export function formatCurrencyWithoutVat(valueWithoutVat: number): {
  withVat: string
  withoutVat: string
  vatLabel: string
} {
  return {
    withVat: formatCurrencyPrecise(addVat(valueWithoutVat)),
    withoutVat: formatCurrencyPrecise(valueWithoutVat),
    vatLabel: VAT_LABEL,
  }
}

/**
 * Formats euros with VAT removed (VAT0 / "Veroton" price)
 * Combines removeVat() and formatEuros() for displaying VAT-exclusive prices
 * @param eurosWithVat - Amount in euros including VAT
 * @returns Formatted VAT-exclusive string (e.g., "119,52 €")
 */
export function formatEurosVat0(
  eurosWithVat: string | number | null | undefined,
): string {
  if (eurosWithVat === null || eurosWithVat === undefined) return '0,00 €'
  const amount =
    typeof eurosWithVat === 'string' ? parseFloat(eurosWithVat) : eurosWithVat
  return formatEuros(removeVat(amount))
}
